/**
 * useBackend Hook - Manages all backend connectivity
 * Handles region fetching, message subscriptions, presence updates
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserClient } from './client';
import { IndexedDBMessageStore } from './idb-message-store';
import type { UserId, RegionId, RegionHeader, ChatMessage, RegionType } from '@maco144/shared';

export interface Piece {
  id: string;
  type: 'nft' | 'location' | 'social' | 'achievement' | 'time' | 'puzzle' | 'public';
  name: string;
  source: string;
  acquiredAt: number;
  metadata?: Record<string, unknown>;
  transferable: boolean;
}

export interface Trove {
  id: string;
  name: string;
  description?: string;
  owner: UserId;
  locked: boolean;
  requiredPieces: Array<{ type: string; count: number }>;
  currentPieces: Array<{ type: string; count: number }>;
  multiPartyRequired: number;
  currentSignatures: number;
  contributors: UserId[];
  reward?: { name: string; description: string };
  createdAt: number;
}

export interface WhisperEntry {
  id: RegionId;
  participantId: UserId;
  lastActivity: number;
}

export interface PendingInvite {
  regionId: RegionId;
  inviterId: UserId;
}

export interface BackendState {
  client: BrowserClient | null;
  userId: UserId | null;
  homeRegion: RegionId | null;
  connected: boolean;
  regions: RegionHeader[];
  currentRegionMessages: Map<RegionId, ChatMessage[]>;
  pieces: Piece[];
  troves: Trove[];
  whispers: WhisperEntry[];
  pendingInvites: PendingInvite[];
  error: string | null;
  loading: boolean;
  loadingMessages: Set<RegionId>; // Track which regions are loading messages from db
}

export interface UseBackendOptions {
  identityUserId?: string;
  identityName?: string;
  passwordHash?: string;
  adjective?: string;
}

export function useBackend(options?: UseBackendOptions) {
  const clientRef = useRef<BrowserClient | null>(null);
  const messageStoreRef = useRef<IndexedDBMessageStore | null>(null);
  const [state, setState] = useState<BackendState>({
    client: null,
    userId: null,
    homeRegion: null,
    connected: false,
    regions: [],
    currentRegionMessages: new Map(),
    pieces: [],
    troves: [],
    whispers: [],
    pendingInvites: [],
    error: null,
    loading: true,
    loadingMessages: new Set(),
  });

  // Initialize client
  useEffect(() => {
    if (clientRef.current) return;

    // Use current domain and protocol (ws:// for HTTP, wss:// for HTTPS)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host; // includes domain and port if non-standard
    const wsUrl = `${protocol}//${host}/`;

    const client = new BrowserClient(wsUrl);
    clientRef.current = client;
    setState((s) => ({ ...s, client }));

    const handleConnected = async () => {
      // console.log('✅ Connected to server');
      setState((s) => ({ ...s, connected: true, error: null }));

      // Authenticate with identity if provided
      if (options?.identityUserId && options?.passwordHash && clientRef.current) {
        try {
          await clientRef.current.authenticateIdentity(
            options.identityUserId,
            options.identityName || 'User',
            options.passwordHash,
            options.adjective
          );
          // After identity auth, manually trigger region/whisper loads since
          // 'authenticated' event fires but legacy 'auth_ok' listener may not catch it
          if (clientRef.current) {
            clientRef.current.listRegions();
            clientRef.current.listWhispers();
          }
        } catch (err) {
          console.error('❌ Identity authentication failed:', err);
          setState((s) => ({
            ...s,
            error: err instanceof Error ? err.message : 'Authentication failed',
          }));
        }
      }
    };

    const handleAuthOk = (userId: UserId, homeRegion: RegionId) => {
      // console.log('✅ Authenticated:', userId);

      // Initialize message store for this user
      messageStoreRef.current = new IndexedDBMessageStore(userId);
      messageStoreRef.current.init().catch((err) => {
        console.error('❌ Failed to initialize message store:', err);
      });

      setState((s) => ({
        ...s,
        userId,
        homeRegion,
        error: null,
      }));
      // Request list of all regions and whispers
      client.listRegions();
      client.listWhispers();
    };

    const handleRegions = (regions: RegionHeader[]) => {
      // console.log('📋 Regions:', regions.length);
      setState((s) => ({ ...s, regions, loading: false }));
    };

    const handleChatMessage = (message: ChatMessage) => {
      // Persist to IndexedDB
      if (messageStoreRef.current) {
        messageStoreRef.current.save(message).catch((err) => {
          console.error('❌ Failed to save message to IndexedDB:', err);
        });
      }

      setState((s) => {
        const messages = new Map(s.currentRegionMessages);
        const regionMessages = messages.get(message.regionId) || [];
        messages.set(message.regionId, [...regionMessages, message]);
        return { ...s, currentRegionMessages: messages };
      });
    };

    const handleChatHistory = (regionId: RegionId, messages: ChatMessage[]) => {
      // console.log(`📝 Chat history for ${regionId}:`, messages.length);

      // Persist received history to IndexedDB
      if (messageStoreRef.current) {
        messages.forEach((msg) => {
          messageStoreRef.current!.save(msg).catch((err) => {
            console.error('❌ Failed to save history message:', err);
          });
        });
      }

      setState((s) => {
        const map = new Map(s.currentRegionMessages);
        map.set(regionId, messages);
        return { ...s, currentRegionMessages: map };
      });
    };

    // Whisper event handlers
    const handleWhisperCreated = (regionId: RegionId, participantId: UserId) => {
      setState((s) => {
        const exists = s.whispers.some((w) => w.id === regionId);
        if (exists) return s;
        return {
          ...s,
          whispers: [...s.whispers, { id: regionId, participantId, lastActivity: Date.now() }],
        };
      });
      // Auto-enter new whisper region to receive messages
      if (clientRef.current) {
        clientRef.current
          .enter(regionId)
          .then(async () => {
            // Load own receipt history from IndexedDB first
            if (messageStoreRef.current) {
              const persisted = await messageStoreRef.current.query({ regionId });
              if (persisted.length > 0) {
                setState((s) => {
                  const map = new Map(s.currentRegionMessages);
                  map.set(regionId, persisted);
                  return { ...s, currentRegionMessages: map };
                });
              }
            }
            clientRef.current?.requestHistory(regionId, 0, 50);
          })
          .catch(() => {});
      }
    };

    const handleWhisperInvite = (regionId: RegionId, inviterId: UserId) => {
      setState((s) => {
        const exists = s.pendingInvites.some((i) => i.regionId === regionId);
        if (exists) return s;
        return {
          ...s,
          pendingInvites: [...s.pendingInvites, { regionId, inviterId }],
        };
      });
    };

    const handleWhispers = (
      whispers: Array<{ id: RegionId; participantId: UserId; lastActivity: number }>
    ) => {
      setState((s) => ({
        ...s,
        whispers: whispers.map((w) => ({
          id: w.id,
          participantId: w.participantId,
          lastActivity: w.lastActivity,
        })),
      }));
      // Enter all existing whisper regions to receive messages
      whispers.forEach((w) => {
        if (clientRef.current) {
          const regionId = w.id;
          clientRef.current
            .enter(regionId)
            .then(async () => {
              // Load receipt history from IndexedDB first (user's own cord)
              if (messageStoreRef.current) {
                const persisted = await messageStoreRef.current.query({ regionId });
                if (persisted.length > 0) {
                  setState((s) => {
                    const map = new Map(s.currentRegionMessages);
                    map.set(regionId, persisted);
                    return { ...s, currentRegionMessages: map };
                  });
                }
              }
              clientRef.current?.requestHistory(regionId, 0, 50);
            })
            .catch(() => {});
        }
      });
    };

    const handleWhisperDeleted = (regionId: RegionId) => {
      setState((s) => ({
        ...s,
        whispers: s.whispers.filter((w) => w.id !== regionId),
        pendingInvites: s.pendingInvites.filter((i) => i.regionId !== regionId),
      }));
    };

    const handlePieceClaimed = (piece: Piece) => {
      // console.log('💎 Piece claimed:', piece.name);
      setState((s) => ({
        ...s,
        pieces: [...s.pieces, piece],
      }));
    };

    const handleTroveUnlocked = (trove: Trove) => {
      // console.log('🔓 Trove unlocked:', trove.name);
      setState((s) => {
        const troves = s.troves.map((t) => (t.id === trove.id ? trove : t));
        return { ...s, troves };
      });
    };

    const handlePiecesUpdate = (pieces: Piece[]) => {
      // console.log('💎 Pieces updated:', pieces.length);
      setState((s) => ({ ...s, pieces }));
    };

    const handleTrovesUpdate = (troves: Trove[]) => {
      // console.log('🗝️ Troves updated:', troves.length);
      setState((s) => ({ ...s, troves }));
    };

    const handleError = (error: string) => {
      console.error('❌ Server error:', error);
      setState((s) => ({ ...s, error, loading: false }));
    };

    const handleDisconnected = () => {
      // console.log('❌ Disconnected from server');
      setState((s) => ({ ...s, connected: false }));
    };

    const handleChannelCreated = (channel: RegionHeader) => {
      // console.log('✨ Channel created:', channel.id);
      // Add new region to regions list
      setState((s) => ({
        ...s,
        regions: [...s.regions, channel],
      }));
    };

    client.on('connected', handleConnected);
    // 'auth_ok' is emitted for spatial-solution auth; 'authenticated' for identity auth
    client.on('auth_ok', handleAuthOk);
    client.on('authenticated', handleAuthOk);
    client.on('regions', handleRegions);
    // Client emits camelCase events for server snake_case messages
    client.on('chat_message', handleChatMessage);
    client.on('chatMessage', handleChatMessage);
    client.on('chat_history', handleChatHistory);
    client.on('chatHistory', handleChatHistory);
    client.on('piece_claimed', handlePieceClaimed);
    client.on('trove_unlocked', handleTroveUnlocked);
    client.on('pieces_update', handlePiecesUpdate);
    client.on('troves_update', handleTrovesUpdate);
    client.on('error', handleError);
    client.on('disconnected', handleDisconnected);
    client.on('channelCreated', handleChannelCreated);
    // Whisper/Accord events
    client.on('whisperCreated', handleWhisperCreated);
    client.on('whisperInvite', handleWhisperInvite);
    client.on('whispers', handleWhispers);
    client.on('whisperDeleted', handleWhisperDeleted);

    client.connect().catch((e) => {
      console.error('Failed to connect:', e);
      setState((s) => ({ ...s, error: e.message, loading: false }));
    });

    return () => {
      client.off('connected', handleConnected);
      client.off('auth_ok', handleAuthOk);
      client.off('authenticated', handleAuthOk);
      client.off('regions', handleRegions);
      client.off('chat_message', handleChatMessage);
      client.off('chatMessage', handleChatMessage);
      client.off('chat_history', handleChatHistory);
      client.off('chatHistory', handleChatHistory);
      client.off('piece_claimed', handlePieceClaimed);
      client.off('trove_unlocked', handleTroveUnlocked);
      client.off('pieces_update', handlePiecesUpdate);
      client.off('troves_update', handleTrovesUpdate);
      client.off('error', handleError);
      client.off('disconnected', handleDisconnected);
      client.off('channelCreated', handleChannelCreated);
      client.off('whisperCreated', handleWhisperCreated);
      client.off('whisperInvite', handleWhisperInvite);
      client.off('whispers', handleWhispers);
      client.off('whisperDeleted', handleWhisperDeleted);
    };
  }, []);

  // Helper: Enter a region and fetch its messages
  const enterRegion = useCallback(async (regionId: RegionId) => {
    if (!clientRef.current) return;
    // console.log('→ Entering region:', regionId);

    // Mark region as loading messages
    setState((s) => {
      const loadingMessages = new Set(s.loadingMessages);
      loadingMessages.add(regionId);
      return { ...s, loadingMessages };
    });

    try {
      await clientRef.current.enter(regionId);

      // Load messages from IndexedDB first
      if (messageStoreRef.current) {
        const persistedMessages = await messageStoreRef.current.query({ regionId });
        // console.log(`📚 Loaded ${persistedMessages.length} persisted messages for ${regionId}`);

        setState((s) => {
          const messages = new Map(s.currentRegionMessages);
          messages.set(regionId, persistedMessages);
          return { ...s, currentRegionMessages: messages };
        });
      }

      // Request additional history from server (limit to last 50)
      clientRef.current.requestHistory(regionId, 0, 50);
    } finally {
      // Clear loading state
      setState((s) => {
        const loadingMessages = new Set(s.loadingMessages);
        loadingMessages.delete(regionId);
        return { ...s, loadingMessages };
      });
    }
  }, []);

  // Helper: Send a message to current region
  const sendMessage = useCallback(async (regionId: RegionId, text: string) => {
    if (!clientRef.current) return;
    const userId = clientRef.current.getUserId() || state.userId;
    if (!userId) return;
    // Server fills in worldRoot, regionRoot, proof, id — echoes back as a confirmed receipt
    clientRef.current.sendChatMessage({
      regionId,
      sender: userId,
      text,
      timestamp: Date.now(),
    });
    // No optimistic insert — server echoes back the enriched message with Merkle proof,
    // which is saved to IndexedDB (the user's receipt store) by handleChatMessage
  }, [state.userId]);

  // Helper: Create a new region
  const createRegion = useCallback(
    async (name: string, type: RegionType, description?: string, isPublic: boolean = true) => {
      if (!clientRef.current) {
        throw new Error('Not connected to server');
      }
      // console.log('✨ Creating region:', name, type);
      clientRef.current.createRegion(type, isPublic, name);
    },
    []
  );

  // Helper: Claim a piece with proof
  const claimPiece = useCallback(
    async (source: string, proof: unknown) => {
      if (!clientRef.current || !state.userId) return;
      // console.log('💎 Claiming piece from source:', source);
      clientRef.current.emit('claim_piece', {
        userId: state.userId,
        source,
        proof,
        timestamp: Date.now(),
      });
    },
    [state.userId]
  );

  // Helper: Contribute pieces to a trove
  const contributePiecesToTrove = useCallback(
    async (troveId: string, pieceIds: string[]) => {
      if (!clientRef.current || !state.userId) return;
      // console.log('🗝️ Contributing pieces to trove:', troveId);
      clientRef.current.emit('contribute_pieces', {
        userId: state.userId,
        troveId,
        pieceIds,
        timestamp: Date.now(),
      });
    },
    [state.userId]
  );

  // Whisper/Accord helpers
  const createWhisper = useCallback((targetUserId: UserId) => {
    clientRef.current?.createWhisper(targetUserId);
  }, []);

  const listWhispers = useCallback(() => {
    clientRef.current?.listWhispers();
  }, []);

  const acceptWhisper = useCallback(async (regionId: RegionId) => {
    // Move from pendingInvites to whispers, then enter the region
    setState((s) => {
      const invite = s.pendingInvites.find((i) => i.regionId === regionId);
      const participantId = invite?.inviterId ?? ('' as UserId);
      return {
        ...s,
        pendingInvites: s.pendingInvites.filter((i) => i.regionId !== regionId),
        whispers: s.whispers.some((w) => w.id === regionId)
          ? s.whispers
          : [...s.whispers, { id: regionId, participantId, lastActivity: Date.now() }],
      };
    });
    if (clientRef.current) {
      try {
        await clientRef.current.enter(regionId);
        clientRef.current.requestHistory(regionId, 0, 50);
      } catch {
        // Ignore — server will send denied if not a participant
      }
    }
  }, []);

  const declineWhisper = useCallback((regionId: RegionId) => {
    clientRef.current?.deleteWhisper(regionId);
  }, []);

  return {
    ...state,
    enterRegion,
    sendMessage,
    createRegion,
    claimPiece,
    contributePiecesToTrove,
    createWhisper,
    listWhispers,
    acceptWhisper,
    declineWhisper,
  };
}
